import React, { useEffect, useState } from 'react';

/**
 * Nhận diện của trình xem theo bộ thương hiệu đang dùng của RIS (Hệ thống > Thương hiệu): tên, logo, favicon, màu nhấn.
 * Đơn vị đổi bộ trên RIS là trình xem đổi theo, không phải build lại. Không đọc được (trình xem không do RIS phục vụ,
 * RIS chưa khai origin trình xem cho CORS…) thì giữ bộ trung tính của index.html — không hiện lại nhận diện OHIF.
 */
type ThuongHieuRis = {
  ten: string;
  logo: string;
  mauNenLogo: string;
};

const MAU_HEX = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i;

let hienTai: ThuongHieuRis | null = null;
const nguoiNghe = new Set<(t: ThuongHieuRis) => void>();

function useThuongHieu(): ThuongHieuRis | null {
  const [t, setT] = useState(hienTai);
  useEffect(() => {
    nguoiNghe.add(setT);
    setT(hienTai);
    return () => {
      nguoiNghe.delete(setT);
    };
  }, []);
  return t;
}

export async function napThuongHieu(): Promise<void> {
  const goc = ((window as any).config?.saveSrApi?.baseUrl || '').replace(/\/+$/, '');
  if (!goc) {
    return;
  }
  try {
    const res = await fetch(`${goc}/api/ohif/thuong-hieu`);
    if (!res.ok) {
      return;
    }
    const t = await res.json();
    // RIS trả đường dẫn tương đối với chính nó; ảnh tải lên có thể là URL đầy đủ. Thứ khác (javascript:, data:…) bỏ.
    const tuyetDoi = (p?: string) =>
      !p ? '' : /^https?:\/\//i.test(p) ? p : p.startsWith('/') ? goc + p : '';

    hienTai = {
      ten: t.ten || '',
      logo: tuyetDoi(t.logo),
      mauNenLogo: MAU_HEX.test(t.mauNenLogo || '') ? t.mauNenLogo : '',
    };
    if (hienTai.ten) {
      document.title = `Xem ảnh DICOM · ${hienTai.ten}`;
    }
    datFavicon(tuyetDoi(t.favicon) || hienTai.logo);
    datMauNhan(t.mauNhan);
    nguoiNghe.forEach(cb => cb(hienTai!));
  } catch {
    // giữ bộ trung tính
  }
}

function datFavicon(href: string) {
  if (!href) {
    return;
  }
  document.querySelectorAll('link[rel~="icon"]').forEach(l => l.remove());
  const link = document.createElement('link');
  link.rel = 'icon';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Màu nhấn của bộ thương hiệu (màn đăng nhập) — chỉ áp khi đủ sáng trên nền tối của trình xem; màu tối (xanh đậm,
 * đen…) đặt lên nền #1f2125 là nút đang chọn chìm mất, thà giữ màu cam mặc định.
 * Tailwind của OHIF đọc --primary dạng bộ ba HSL, CSS riêng của mình đọc --ris-primary dạng hex (xem App.css).
 */
function datMauNhan(hex?: string) {
  const m = MAU_HEX.exec(hex || '');
  if (!m) {
    return;
  }
  const [r, g, b] = m.slice(1).map(x => parseInt(x, 16) / 255);
  const tuyenTinh = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  const doSang = 0.2126 * tuyenTinh(r) + 0.7152 * tuyenTinh(g) + 0.0722 * tuyenTinh(b);
  if (doSang < 0.2) {
    return;
  }

  const lon = Math.max(r, g, b);
  const nho = Math.min(r, g, b);
  const l = (lon + nho) / 2;
  const d = lon - nho;
  const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
  let h = 0;
  if (d !== 0) {
    h = lon === r ? ((g - b) / d) % 6 : lon === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
  }
  const goc = document.documentElement.style;
  goc.setProperty('--ris-primary', hex!);
  goc.setProperty('--primary', `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`);
}

/** Logo đầu trang (header + danh sách ca). Chưa có / không đọc được bộ thương hiệu thì để trống. */
export function LogoThuongHieu() {
  const t = useThuongHieu();
  if (!t?.logo) {
    return null;
  }
  // no-referrer: logo có thể nằm ở máy khác, còn URL trang trình xem mang giấy phép (srToken, ghiTk, tk).
  return (
    <img
      src={t.logo}
      alt={t.ten}
      referrerPolicy="no-referrer"
      className="ml-2 h-8 max-w-[160px] rounded object-contain"
      style={t.mauNenLogo ? { background: t.mauNenLogo, padding: '2px 6px' } : undefined}
    />
  );
}

export function TenThuongHieu() {
  const t = useThuongHieu();
  return <>{t?.ten || 'Xem ảnh DICOM'}</>;
}
